import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Lightbulb, ArrowRight } from 'lucide-react';

const PRIORITY_OPTIONS = [
  { value: 'HANDLE_NOW', label: 'Handle Now', hint: 'Urgent — work next' },
  { value: 'DUE_TODAY',  label: 'Due Today',  hint: 'Resolve during this shift' },
  { value: 'WAITING_ON', label: 'Waiting On', hint: 'Pending guest / vendor / team' },
  { value: 'SCHEDULED',  label: 'Scheduled',  hint: 'Planned for a future time' },
];

const CATEGORY_OPTIONS = [
  'ARRIVAL', 'ACCESS', 'MAINTENANCE', 'HOUSEKEEPING', 'TRANSPORT',
  'BOOKING', 'CONCIERGE', 'BILLING', 'INQUIRY', 'IN_STAY_ISSUE', 'OTHER',
];

const OWNER_OPTIONS = [
  { value: 'front_desk',     label: 'Front Desk (control point)' },
  { value: 'unassigned',     label: 'Unassigned (decide later)' },
  { value: 'housekeeping',   label: 'Housekeeping' },
  { value: 'maintenance',    label: 'Maintenance' },
  { value: 'manager_on_duty', label: 'Manager on Duty' },
  { value: 'concierge',      label: 'Concierge' },
  { value: 'transport',      label: 'Transport / Driver' },
  { value: 'reservations',   label: 'Reservations' },
];

const ROUTE_TO_OPTIONS = [
  { value: 'front_desk',     label: 'Front Desk' },
  { value: 'housekeeping',   label: 'Housekeeping' },
  { value: 'maintenance',    label: 'Maintenance' },
  { value: 'transport',      label: 'Transport / Driver' },
  { value: 'reservations',   label: 'Reservations' },
  { value: 'concierge',      label: 'Concierge' },
];

// Mirror of the backend ROUTE_TO_BY_REQUEST_TYPE / BY_CATEGORY tables so the
// modal can show the suggestion before submit. Backend remains source of truth.
const ROUTE_BY_REQUEST_TYPE = {
  LATE_CHECKOUT: 'reservations',
  TRANSPORT:     'transport',
  MAINTENANCE:   'maintenance',
  HOUSEKEEPING:  'housekeeping',
  SAFETY:        'front_desk',
  ACCESS:        'front_desk',
  CHECKIN:       'front_desk',
  OTHER:         'front_desk',
};
const ROUTE_BY_CATEGORY = {
  MAINTENANCE:   'maintenance',
  HOUSEKEEPING:  'housekeeping',
  TRANSPORT:     'transport',
  ACCESS:        'front_desk',
  ARRIVAL:       'front_desk',
  BOOKING:       'reservations',
  CONCIERGE:     'concierge',
  BILLING:       'front_desk',
  INQUIRY:       'front_desk',
  IN_STAY_ISSUE: 'front_desk',
  OTHER:         'front_desk',
};
const suggestRouteTo = (category, requestType) => {
  if (requestType && ROUTE_BY_REQUEST_TYPE[requestType.toUpperCase()]) return ROUTE_BY_REQUEST_TYPE[requestType.toUpperCase()];
  if (category && ROUTE_BY_CATEGORY[category.toUpperCase()]) return ROUTE_BY_CATEGORY[category.toUpperCase()];
  return 'front_desk';
};

/**
 * AddToRunModal — confirm-before-create modal for the Guest Inbox.
 *
 * Maris suggests, staff confirms. The form is pre-filled from the
 * conversation's intake metadata; every field is overridable.
 */
export const AddToRunModal = ({ open, onOpenChange, conversation, onConfirm }) => {
  const suggestedPriority = conversation?.intake_priority || 'DUE_TODAY';
  const suggestedCategory = conversation?.intake_category || 'OTHER';
  const suggestedRouteTo = suggestRouteTo(suggestedCategory, conversation?.request_type);

  const [priority, setPriority] = useState(suggestedPriority);
  const [category, setCategory] = useState(suggestedCategory);
  // Front Desk is the default owner — control point that routes the work.
  const [owner, setOwner] = useState('front_desk');
  const [routeTo, setRouteTo] = useState(suggestedRouteTo);
  const [dueAt, setDueAt] = useState('');
  const [nextTouchAt, setNextTouchAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the modal opens for a different conversation
  useEffect(() => {
    if (open) {
      const nextCategory = conversation?.intake_category || 'OTHER';
      setPriority(conversation?.intake_priority || 'DUE_TODAY');
      setCategory(nextCategory);
      setOwner('front_desk');
      setRouteTo(suggestRouteTo(nextCategory, conversation?.request_type));
      setDueAt('');
      setNextTouchAt('');
    }
  }, [open, conversation?.id, conversation?.intake_priority, conversation?.intake_category, conversation?.request_type]);

  // When the user changes the category, refresh the route-to suggestion to
  // match — unless they've already manually overridden it away from the
  // current suggestion. Simpler: always refresh on category change.
  useEffect(() => {
    if (open) setRouteTo(suggestRouteTo(category, conversation?.request_type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const toIso = (local) => {
    if (!local) return null;
    // <input type="datetime-local"> returns "YYYY-MM-DDTHH:mm"
    const d = new Date(local);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        priority,
        category,
        // 'unassigned' on the UI maps to a null owner so backend default applies.
        assigned_to_user_id: owner === 'unassigned' ? null : owner,
        route_to: routeTo,
        dueAt: toIso(dueAt),
        next_touch_at: toIso(nextTouchAt),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const suggestedPriorityLabel = PRIORITY_OPTIONS.find(p => p.value === suggestedPriority)?.label || suggestedPriority;
  const suggestedRouteLabel = ROUTE_TO_OPTIONS.find(r => r.value === suggestedRouteTo)?.label || suggestedRouteTo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-to-run-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Add to Daily Run</DialogTitle>
          <DialogDescription>
            Front Desk owns by default — adjust the route and details before confirming.
          </DialogDescription>
        </DialogHeader>

        {/* Maris suggestion summary */}
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600 flex items-start gap-2">
          <Lightbulb className="h-4 w-4 text-[#3F617E] mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-medium text-stone-800">Maris suggests:</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{suggestedPriorityLabel}</Badge>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{suggestedCategory.replace(/_/g, ' ')}</Badge>
              <span className="text-stone-400">·</span>
              <span className="text-stone-500">Owner</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#3F617E]/10 border-[#3F617E]/30 text-[#3F617E]">Front Desk</Badge>
              <span className="text-stone-500">Route to</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{suggestedRouteLabel}</Badge>
            </div>
            {conversation?.guestName && (
              <div><span className="text-stone-500">Guest:</span> {conversation.guestName}</div>
            )}
            {conversation?.intake_summary && (
              <div className="line-clamp-2"><span className="text-stone-500">Request:</span> {conversation.intake_summary}</div>
            )}
          </div>
        </div>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="add-to-run-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    <div className="flex flex-col">
                      <span>{o.label}</span>
                      <span className="text-[10px] text-stone-500">{o.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="add-to-run-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map(c => (
                  <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Owner</Label>
              <Select value={owner} onValueChange={setOwner}>
                <SelectTrigger data-testid="add-to-run-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OWNER_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-stone-400 leading-snug">Owns follow-through. Front Desk by default.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Route to</Label>
              <Select value={routeTo} onValueChange={setRouteTo}>
                <SelectTrigger data-testid="add-to-run-route-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROUTE_TO_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-stone-400 leading-snug">Department that handles the work.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Due time (optional)</Label>
              <Input
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                data-testid="add-to-run-due-at"
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Next touch (optional)</Label>
              <Input
                type="datetime-local"
                value={nextTouchAt}
                onChange={(e) => setNextTouchAt(e.target.value)}
                data-testid="add-to-run-next-touch"
                className="h-9 text-sm"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="rounded-full"
            data-testid="add-to-run-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
            data-testid="add-to-run-confirm"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-1" />
            )}
            Add to Daily Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToRunModal;
